import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@/common/contexts/AuthContext';
import {
  ChildrenDefaultProps,
  ChildrenPropTypes,
} from '@/common/proptypes/Base/Children.proptypes';
import ScanbatUserSession from '@/common/services/ScanbatUserSession';

const ScanbatSessionContext = createContext(null);

const ScanbatSessionProvider = ({ children }) => {
  const { logout } = useAuth();
  const sessionSubscriberIdRef = useRef(null);

  const [isSessionWarning, setIsSessionWarning] = useState(false);
  const [isSessionEnded, setIsSessionEnded] = useState(false);

  const handleSessionWarning = useCallback(() => {
    setIsSessionEnded(false);
    setIsSessionWarning(true);
  }, []);

  const handleSessionEnded = useCallback(() => {
    setIsSessionWarning(false);
    setIsSessionEnded(true);
  }, []);

  const handleRestartSession = () => {
    setIsSessionWarning(false);
    setIsSessionEnded(false);
    ScanbatUserSession.startSession();
  };

  const handleLogout = () => {
    setIsSessionWarning(false);
    setIsSessionEnded(false);
    logout();
  };

  useEffect(() => {
    ScanbatUserSession.unsubscribe(sessionSubscriberIdRef.current);
    sessionSubscriberIdRef.current = ScanbatUserSession.subscribe(
      handleSessionWarning,
      handleSessionEnded,
    );
  }, [handleSessionEnded, handleSessionWarning]);

  useEffect(
    () => () => {
      ScanbatUserSession.unsubscribe(sessionSubscriberIdRef.current);
    },
    [],
  );

  return (
    <ScanbatSessionContext.Provider
      value={{
        isSessionWarning,
        isSessionEnded,
        restartSession: handleRestartSession,
        logout: handleLogout,
      }}
    >
      {children}
    </ScanbatSessionContext.Provider>
  );
};

ScanbatSessionProvider.defaultProps = {
  ...ChildrenDefaultProps,
};

ScanbatSessionProvider.propTypes = {
  ...ChildrenPropTypes,
};

export const useScanbatSessionContext = () => useContext(ScanbatSessionContext);

export default ScanbatSessionProvider;
